import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FiInfo, FiSettings, FiRotateCcw, FiGrid, FiBell } from 'react-icons/fi';
import { defaultAppSettings, getAppSettings, saveAppSettings } from '../utils/settings';

const SettingToggleRow = ({ icon, label, description, tooltip, checked, onChange, isLast = false }) => (
  <div className={`d-flex align-items-start justify-content-between gap-3 py-3 ${isLast ? '' : 'border-bottom'}`}>
    <div className="d-flex gap-3">
      <div className="icon-chip icon-chip-sm icon-chip-secondary mt-1">{icon}</div>
      <div>
        <div className="fw-semibold d-flex align-items-center gap-2">
          {label}
          <OverlayTrigger placement="top" overlay={<Tooltip id={`${label}-tip`}>{tooltip}</Tooltip>}>
            <span className="text-muted" style={{ cursor: 'help' }}>
              <FiInfo size={13} />
            </span>
          </OverlayTrigger>
        </div>
        <div className="text-muted small">{description}</div>
      </div>
    </div>
    <Form.Check type="switch" checked={checked} onChange={onChange} className="fs-5" />
  </div>
);

const SettingsPage = () => {
  const [settings, setSettings] = useState(defaultAppSettings);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings(getAppSettings());
  }, []);

  const updateSetting = (key, value) => {
    setSaved(false);
    setSettings((prev) => ({ ...prev, [key]: value }));
    if (key === 'compactTables') {
      document.body.classList.toggle('compact-tables', Boolean(value));
    }
  };

  const handleSave = () => {
    saveAppSettings(settings);
    globalThis.dispatchEvent(new Event('appSettingsChanged'));
    setSaved(true);
  };

  const handleResetToDefaults = () => {
    setSettings(defaultAppSettings);
    setSaved(false);
    // Apply default settings immediately to the UI
    document.body.classList.toggle('compact-tables', Boolean(defaultAppSettings.compactTables));
  };

  return (
    <Container fluid className="py-4 settings-page">
      <div className="mb-4">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle mb-0">Personal preferences for how the app looks and behaves for you.</p>
      </div>

      <Card className="border-0 shadow-sm card-accent-top">
        <Card.Body>
          <div className="section-header-row">
            <div className="icon-chip icon-chip-sm">
              <FiSettings />
            </div>
            <h5 className="section-header-title">General</h5>
          </div>

          {saved && <Alert variant="success">Settings saved successfully.</Alert>}

          <Row className="g-3 mb-2">
            <Col md={6}>
              <Form.Label>Default Landing Page</Form.Label>
              <Form.Select
                value={settings.defaultLandingPage}
                onChange={(e) => updateSetting('defaultLandingPage', e.target.value)}
              >
                <option value="dashboard">Dashboard</option>
                <option value="projects">Projects</option>
                <option value="help">Help & Support</option>
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label>Date Format</Form.Label>
              <Form.Select
                value={settings.dateFormat}
                onChange={(e) => updateSetting('dateFormat', e.target.value)}
              >
                <option value="en-IN">India (DD/MM/YYYY)</option>
                <option value="en-US">US (MM/DD/YYYY)</option>
              </Form.Select>
            </Col>
          </Row>

          <div className="mt-2">
            <SettingToggleRow
              icon={<FiGrid />}
              label="Compact tables"
              description="Makes table rows denser by reducing cell padding. Applies immediately; click Save to keep this preference."
              tooltip="Makes table rows denser by reducing cell padding."
              checked={settings.compactTables}
              onChange={(e) => updateSetting('compactTables', e.target.checked)}
            />
            <SettingToggleRow
              icon={<FiBell />}
              label="Show notifications"
              description="Controls whether the notification bell/panel is visible in the top bar (admin view)."
              tooltip="Controls whether the notification bell/panel is visible in the top bar."
              checked={settings.showNotifications}
              onChange={(e) => updateSetting('showNotifications', e.target.checked)}
              isLast
            />
          </div>

          <div className="d-flex gap-2 mt-4">
            <Button variant="primary" onClick={handleSave}>Save Settings</Button>
            <Button variant="outline-secondary" onClick={handleResetToDefaults}>
              <FiRotateCcw className="me-2" />
              Reset to Defaults
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SettingsPage;
